import { TaskDefinition } from '@/feature/execution-board'
import { getPointCoordinates } from '@/utils/geoFenceConverters'

export type TaskMarker = {
  id: string
  locationId: string
  name: string
  locationType: string
  locationObjectId: string | number | null
  position: google.maps.LatLngLiteral
}

type TaskLocationGeoJson = TaskDefinition['taskDefinition']['locations'][number]['geoJson']

// Resolves a task location's map coordinates from its geoJson: a top-level Point geometry first,
// then a `properties.home` fallback, else null when neither is present.
export const getLocationCoordinates = (
  geoJson: TaskLocationGeoJson,
): google.maps.LatLngLiteral | null => {
  if (!geoJson) {
    return null
  }
  return (
    getPointCoordinates(geoJson) ??
    (geoJson?.properties?.home?.lat != null
      ? {
          lat: geoJson.properties.home.lat,
          lng: geoJson.properties.home.lng,
        }
      : null)
  )
}

/**
 * Derives the renderable map markers for a task from its location geoJson.
 * Locations without resolvable coordinates are skipped. Order is preserved so
 * index 0 is the start and the last entry is the destination.
 */
export const getTaskMarkers = (task: TaskDefinition): TaskMarker[] => {
  const locations = task.taskDefinition.locations
  if (!locations) {
    return []
  }

  return locations
    .map((location, index): TaskMarker | null => {
      const position = getLocationCoordinates(location.geoJson)
      if (!position) {
        return null
      }
      return {
        id: `task-marker-${task.id}-location-${index}`,
        locationId: location.id,
        name: location.name,
        locationType: location.type,
        locationObjectId: location.originalObjectId,
        position,
      }
    })
    .filter((marker): marker is TaskMarker => marker !== null)
}
